/**
 * Chef/vendor weekly working-schedule model — the frontend mirror of
 * `profile.v1.VendorSchedule`. Kept React-free so the schedule editor, the chef
 * surfaces, and tests can all share the same shape and helpers. The schedule is
 * persisted via the `profile:set_vendor_schedule` command and flows back on the
 * `vendor_profiles` projection as `record.schedule`.
 */

import { parseTimestamp } from './timestamp'

/** ISO weekday numbering (Mon=1 … Sun=7), matching proto `Weekday`. */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7

export interface WorkingDay {
  day: Weekday
  /** Whether the vendor trades this day. */
  open: boolean
  /** "HH:MM" 24-hour local open time (meaningful only when `open`). */
  openTime: string
  /** "HH:MM" 24-hour local close time (meaningful only when `open`). */
  closeTime: string
}

export interface WeeklySchedule {
  days: WorkingDay[]
  /** IANA timezone the hours are expressed in; empty means market default. */
  timezone: string
}

export const WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 7]

const DAY_LABELS: Record<Weekday, { short: string; long: string }> = {
  1: { short: 'Mon', long: 'Monday' },
  2: { short: 'Tue', long: 'Tuesday' },
  3: { short: 'Wed', long: 'Wednesday' },
  4: { short: 'Thu', long: 'Thursday' },
  5: { short: 'Fri', long: 'Friday' },
  6: { short: 'Sat', long: 'Saturday' },
  7: { short: 'Sun', long: 'Sunday' },
}

export const dayLabel = (day: Weekday, form: 'short' | 'long' = 'short'): string =>
  DAY_LABELS[day][form]

const HHMM = /^([01][0-9]|2[0-3]):[0-5][0-9]$/

/** A sensible starting week for a new chef: Mon–Sat 9–5, closed Sunday. */
export const defaultSchedule = (): WeeklySchedule => ({
  days: WEEKDAYS.map((day) => ({
    day,
    open: day !== 7,
    openTime: '09:00',
    closeTime: '17:00',
  })),
  timezone: 'Africa/Lagos',
})

/** True when a day's window is well-formed and close is after open. */
export const isValidDay = (d: WorkingDay): boolean =>
  !d.open || (HHMM.test(d.openTime) && HHMM.test(d.closeTime) && d.closeTime > d.openTime)

/** The whole schedule is saveable only when every open day is well-formed. */
export const isValidSchedule = (s: WeeklySchedule): boolean => s.days.every(isValidDay)

/**
 * Parse a schedule off a projection record (`record.schedule`). Returns null
 * when absent or malformed so callers can render an empty state. Missing days
 * are backfilled as closed, so the editor always has all seven.
 */
export const parseSchedule = (raw: unknown): WeeklySchedule | null => {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const rawDays = Array.isArray(obj.days) ? obj.days : null
  if (!rawDays) return null

  const byDay = new Map<Weekday, WorkingDay>()
  for (const item of rawDays) {
    if (!item || typeof item !== 'object') continue
    const entry = item as Record<string, unknown>
    const day = Number(entry.day) as Weekday
    if (!WEEKDAYS.includes(day)) continue
    byDay.set(day, {
      day,
      open: entry.open === true,
      openTime: typeof entry.open_time === 'string' ? entry.open_time : '',
      closeTime: typeof entry.close_time === 'string' ? entry.close_time : '',
    })
  }

  return {
    timezone: typeof obj.timezone === 'string' ? obj.timezone : '',
    days: WEEKDAYS.map(
      (day) => byDay.get(day) ?? { day, open: false, openTime: '', closeTime: '' },
    ),
  }
}

/** Serialize to the snake_case payload the command/backend expects. */
export const toSchedulePayload = (s: WeeklySchedule): Record<string, unknown> => ({
  timezone: s.timezone,
  days: s.days.map((d) => ({
    day: d.day,
    open: d.open,
    open_time: d.open ? d.openTime : '',
    close_time: d.open ? d.closeTime : '',
  })),
})

/**
 * Whether two schedules describe the same working week.
 *
 * Compared by meaning, not by field equality: a closed day's open/close times
 * are not part of what it says, and the backend canonicalizes them away, so a
 * draft carrying leftover "09:00"/"17:00" on a closed day still equals the
 * projection that came back with empty strings. Used to decide when a saved
 * week has actually landed in the projection.
 */
export const schedulesEqual = (a: WeeklySchedule, b: WeeklySchedule): boolean => {
  if ((a.timezone || '') !== (b.timezone || '')) return false
  const signature = (s: WeeklySchedule) =>
    WEEKDAYS.map((day) => {
      const d = s.days.find((entry) => entry.day === day)
      if (!d || !d.open) return 'x'
      return `${d.openTime}-${d.closeTime}`
    }).join('|')
  return signature(a) === signature(b)
}

/** Minutes since midnight for an "HH:MM" string, or null when malformed. */
const toMinutes = (hhmm: string): number | null => {
  if (!HHMM.test(hhmm)) return null
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

/** Weekday short names as `Intl` emits them, mapped to ISO weekday numbers. */
const WEEKDAY_INDEX: Record<string, Weekday> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
}

/**
 * Current ISO weekday (Mon=1…Sun=7) and minutes-since-midnight, read in the
 * schedule's own IANA timezone so "are they open right now?" matches the
 * chef's wall clock, not the viewer's. Falls back to host-local time when the
 * timezone is missing or unknown.
 */
const nowInZone = (timezone: string, now: Date): { day: Weekday; minutes: number } => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || 'Africa/Lagos',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now)
    const wd = parts.find((p) => p.type === 'weekday')?.value ?? ''
    const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 'NaN')
    const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 'NaN')
    const day = WEEKDAY_INDEX[wd]
    if (day && !Number.isNaN(hour) && !Number.isNaN(minute)) {
      // Intl can emit hour "24" at midnight in the hour12:false form.
      return { day, minutes: (hour % 24) * 60 + minute }
    }
  } catch {
    /* invalid timezone — fall through to host-local time */
  }
  const iso = (((now.getDay() + 6) % 7) + 1) as Weekday
  return { day: iso, minutes: now.getHours() * 60 + now.getMinutes() }
}

/**
 * Whether the weekly schedule has an open window covering `now`. A null
 * schedule means "no hours set", which is treated as unrestricted (true) so a
 * chef who never set hours is gated only by their manual open/closed toggle.
 */
export const isOpenNow = (schedule: WeeklySchedule | null, now: Date = new Date()): boolean => {
  if (!schedule) return true
  const { day, minutes } = nowInZone(schedule.timezone, now)
  const today = schedule.days.find((d) => d.day === day)
  if (!today || !today.open) return false
  const open = toMinutes(today.openTime)
  const close = toMinutes(today.closeTime)
  if (open === null || close === null) return false
  return minutes >= open && minutes < close
}

/**
 * Effective open state for a vendor projection row: the kitchen counts as open
 * only when the chef is manually toggled open (`is_open`) AND the current time
 * falls inside their working hours (or they have not set any). This is the
 * single source of truth every surface should use — the chef's status card, the
 * public chef cards/discovery, and the customer ordering gate — so the sign a
 * customer sees always matches what the kitchen can actually take.
 */
export const isVendorOpen = (
  vendor: { is_open?: unknown; schedule?: unknown; is_sick_paused?: unknown } | null | undefined,
  now: Date = new Date(),
): boolean => {
  // A sick pause reads as closed, with no other public signal: reporting
  // illness must never cost the chef visibility or standing (verification
  // spec 3.4). The server's sales gate refuses orders either way.
  if (!vendor || vendor.is_open !== true || vendor.is_sick_paused === true) return false
  return isOpenNow(parseSchedule(vendor.schedule), now)
}

/** Today's working-day entry (in the schedule's timezone), or null. */
export const openWindowToday = (
  schedule: WeeklySchedule | null,
  now: Date = new Date(),
): WorkingDay | null => {
  if (!schedule) return null
  const { day } = nowInZone(schedule.timezone, now)
  return schedule.days.find((d) => d.day === day) ?? null
}

/** "09:00" → "9:00 AM". Display only — storage stays 24-hour. */
export const to12h = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm
  const period = h < 12 ? 'AM' : 'PM'
  const hour = h % 12 === 0 ? 12 : h % 12
  return `${hour}:${String(m).padStart(2, '0')} ${period}`
}

/**
 * One-line human summary of the working week, collapsing runs of identical
 * hours (e.g. "Mon–Fri 9:00 AM–5:00 PM · Sat 10:00 AM–2:00 PM · Sun closed").
 */
export const summarizeSchedule = (s: WeeklySchedule): string => {
  const parts: string[] = []
  let i = 0
  while (i < s.days.length) {
    const d = s.days[i]
    const signature = d.open ? `${d.openTime}-${d.closeTime}` : 'closed'
    let j = i
    while (
      j + 1 < s.days.length &&
      (s.days[j + 1].open ? `${s.days[j + 1].openTime}-${s.days[j + 1].closeTime}` : 'closed') ===
        signature
    ) {
      j += 1
    }
    const span =
      i === j ? dayLabel(s.days[i].day) : `${dayLabel(s.days[i].day)}–${dayLabel(s.days[j].day)}`
    parts.push(d.open ? `${span} ${to12h(d.openTime)}–${to12h(d.closeTime)}` : `${span} closed`)
    i = j + 1
  }
  return parts.join(' · ')
}

/* ── last-confirmed schedule (local hold) ─────────────────────────────── */

/**
 * A chef's own working hours are written through the command lane and read back
 * off the `vendor_profiles` projection, and those two are not the same clock:
 * the row can take a sweep to be mirrored, and a vendor row that has never been
 * projected into this scope never arrives at all. Until it does, every surface
 * that reads the projection — the editor and the Kitchen card — has nothing to
 * show and falls back to the Mon–Sat 9–5 default, which reads as "my hours were
 * thrown away" to the chef who just saved them.
 *
 * So a confirmed write (the command awaited and returned) is also remembered
 * here, keyed by profile, and outranks the projection until the projection can
 * be shown to be newer. It is deliberately durable rather than component state:
 * the previous in-memory hold died on the first navigation, so leaving
 * `/schedule` and coming back showed the default week again.
 */
interface SavedScheduleHold {
  savedAt: number
  schedule: Record<string, unknown>
}

const savedScheduleKey = (profileId: string) => `chow_vendor_schedule:${profileId}`

export const rememberSavedSchedule = (profileId: string, schedule: WeeklySchedule): void => {
  if (!profileId) return
  const hold: SavedScheduleHold = { savedAt: Date.now(), schedule: toSchedulePayload(schedule) }
  try {
    localStorage.setItem(savedScheduleKey(profileId), JSON.stringify(hold))
  } catch {
    /* Storage disabled/full: the hold is an optimisation, never a requirement. */
  }
}

const readHold = (profileId: string): { savedAt: number; schedule: WeeklySchedule } | null => {
  if (!profileId) return null
  try {
    const raw = localStorage.getItem(savedScheduleKey(profileId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as SavedScheduleHold
    const schedule = parseSchedule(parsed?.schedule)
    return schedule ? { savedAt: Number(parsed.savedAt) || 0, schedule } : null
  } catch {
    return null
  }
}

/** The week last confirmed for this profile, if any. */
export const recallSavedSchedule = (profileId: string): WeeklySchedule | null =>
  readHold(profileId)?.schedule ?? null

export const forgetSavedSchedule = (profileId: string): void => {
  if (!profileId) return
  try {
    localStorage.removeItem(savedScheduleKey(profileId))
  } catch {
    /* Nothing to do — see rememberSavedSchedule. */
  }
}

const asMillis = (value: unknown): number => {
  return parseTimestamp(value) ?? 0
}

/* ── last-confirmed open/closed toggle (local hold) ───────────────────── */

interface SavedStatusHold {
  savedAt: number
  isOpen: boolean
}

const savedStatusKey = (profileId: string) => `chow_vendor_status:${profileId}`

/** Remember a toggle the server confirmed — same reasoning as the schedule hold. */
export const rememberVendorStatus = (profileId: string, isOpen: boolean): void => {
  if (!profileId) return
  const hold: SavedStatusHold = { savedAt: Date.now(), isOpen }
  try {
    localStorage.setItem(savedStatusKey(profileId), JSON.stringify(hold))
  } catch {
    /* Storage disabled/full: the hold is an optimisation, never a requirement. */
  }
}

const readStatusHold = (profileId: string): SavedStatusHold | null => {
  if (!profileId) return null
  try {
    const raw = localStorage.getItem(savedStatusKey(profileId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as SavedStatusHold
    if (typeof parsed?.isOpen !== 'boolean') return null
    return { savedAt: Number(parsed.savedAt) || 0, isOpen: parsed.isOpen }
  } catch {
    return null
  }
}

/**
 * The chef's manual open/closed toggle as the kitchen should show it.
 *
 * A missing row or field is CLOSED. `chow_vendor_profiles.is_open` defaults to
 * false and every customer surface reads `is_open === true` (`isVendorOpen`).
 * The Kitchen page used to read `!== false`, so a chef with no row saw "Open"
 * while customers saw "Closed" — and the moment saving hours, opting into meal
 * plans or editing the profile created the row (with the default false), the
 * dashboard flipped to "Offline" on its own. That was "my kitchen keeps going
 * offline".
 *
 * The last confirmed toggle outranks the projection until a projected row is
 * newer than it, because the vendor row may lag a sweep or never be mirrored —
 * without the hold, a reload would show the pre-toggle state.
 */
export const effectiveManualOpen = (
  profileId: string,
  vendor: { is_open?: unknown; updated_at?: unknown } | null | undefined,
): boolean => {
  const projected = vendor?.is_open === true
  const hold = readStatusHold(profileId)
  if (!hold) return projected
  if (vendor && asMillis(vendor.updated_at) > hold.savedAt) return projected
  return hold.isOpen
}

/**
 * The week to show for a chef: the projection when it is trustworthy, otherwise
 * the last write we confirmed.
 *
 * The hold wins by default, because the common disagreement is a projection
 * that has not caught up (or never carries this vendor's row at all) — and
 * showing the pre-save week there is the bug this exists to fix. It yields when
 * the projected row is demonstrably newer than the hold (`updated_at` past the
 * moment we saved), which is a change made on another device or surface.
 *
 * A hold that matches the projection is left in place rather than deleted: it
 * costs nothing, and it is what keeps a reload correct while the vendor row is
 * missing from the mirror — deleting it on a match meant the optimistic store
 * write released the hold immediately, and the very next reload was wrong
 * again.
 *
 * Returns null when neither lane knows anything — a chef who has never set
 * hours, which must surface as "Set hours" rather than an invented week.
 */
export const effectiveSchedule = (
  profileId: string,
  stored: WeeklySchedule | null,
  storedUpdatedAt?: unknown,
): WeeklySchedule | null => {
  const hold = readHold(profileId)
  if (!hold) return stored
  if (!stored) return hold.schedule
  if (schedulesEqual(stored, hold.schedule)) return stored
  if (asMillis(storedUpdatedAt) > hold.savedAt) {
    forgetSavedSchedule(profileId)
    return stored
  }
  return hold.schedule
}
